import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Send } from 'lucide-react';
import { z } from 'zod';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { FilterBar } from '@/components/composed/FilterBar';
import { Button } from '@/components/ui/button';
import {
  EntityContractTable,
  SendContractDialog,
  entityContractListQuery,
} from '@/features/entity-contracts';
import type { EntityContractListParams } from '@/lib/api/domains/entity-contracts';
import { canEdit } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import { usePageMeta } from '@/app/shell/use-page-meta';

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  pageSize: z.number().int().min(1).max(100).catch(25),
  sort: z.string().optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute('/_auth/entity-contracts/')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(
      entityContractListQuery(toParams(deps)),
    ),
  component: EntityContractsPage,
});

function toParams(
  search: z.infer<typeof searchSchema>,
): EntityContractListParams {
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...(search.sort ? { sort: search.sort } : {}),
    ...(search.q ? { q: search.q } : {}),
  };
}

function EntityContractsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const session = useAuthStore((s) => s.session);
  const [sendOpen, setSendOpen] = useState(false);

  function setSearch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  usePageMeta({ title: 'Entity Contracts' });

  return (
    <Page bleed>
      <PageHeader
        title="Entity Contracts"
        actions={
          canEdit(session) ? (
            <Button onClick={() => setSendOpen(true)}>
              <Send className="size-4" />
              Send Contract
            </Button>
          ) : null
        }
      />

      <Section>
        <FilterBar
          search={search.q ?? ''}
          onSearchChange={(q) => setSearch({ q: q || undefined, page: 1 })}
          searchLabel="Search"
          searchPlaceholder="Search by partner…"
          onClear={
            search.q ? () => setSearch({ q: undefined, page: 1 }) : undefined
          }
        />
        <EntityContractTable
          params={toParams(search)}
          onPageChange={(page) => setSearch({ page })}
        />
      </Section>

      <SendContractDialog open={sendOpen} onOpenChange={setSendOpen} />
    </Page>
  );
}
